// client/src/pages/GroupEdit.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import GroupForm from '../components/GroupForm';
import { apiFetch, refreshSessionUser } from '../lib/api';

/* -------------------- Local helpers (Option B – no preview API) -------------------- */
function periodsPerYear(freq) {
  const f = String(freq || '').toLowerCase();
  if (f === 'weekly') return 52;
  if (f === 'fortnightly') return 26;
  return 12; // monthly default
}
function perPeriodRate(annualRate, F) {
  return Math.pow(1 + Number(annualRate || 0.10), 1 / F) - 1; // compounded
}
function roundToStep(x, step, mode = 'ceil') {
  const s = Number(step || 0);
  if (!s || s <= 0) return x;
  if (mode === 'floor')   return Math.floor(x / s) * s;
  if (mode === 'nearest') return Math.round(x / s) * s;
  return Math.ceil(x / s) * s; // default = ceil
}
function buildStartingBidSchedule({
  duration, contribution, frequency,
  bidStep, annualRate, organiserPeriod = 0,
  rounding = 'ceil',
}) {
  const M = Number(duration);
  const C = Number(contribution);
  const P = M * C;
  const F = periodsPerYear(frequency);
  const i = perPeriodRate(annualRate, F);
  const step = Number(bidStep || 100);

  const rows = [];
  for (let p = 1; p <= M; p++) {
    const raw = (p === Number(organiserPeriod || 0))
      ? 0
      : ((M - 1) / M) * P * (1 - Math.pow(1 + i, p - M));

    let rec = roundToStep(raw, step, rounding);
    if (p < M && raw > 0 && rec === 0 && step > 0) rec = step; // only last is 0
    if (p === M || p === Number(organiserPeriod || 0)) rec = 0;

    rows.push({ period: p, starting_bid: Math.max(0, Number.isFinite(rec) ? rec : 0) });
  }
  return rows;
}
/* ----------------------------------------------------------------------------------- */

export default function GroupEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [initial, setInitial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  // start with cached user, then refresh once for accuracy
  const cachedUser = useMemo(() => {
    try { return JSON.parse(sessionStorage.getItem('user')); } catch { return null; }
  }, []);
  const [user, setUser] = useState(cachedUser);

  useEffect(() => {
    (async () => {
      try {
        const fresh = await refreshSessionUser();
        setUser(fresh);
      } catch {
        // ignore; we still rely on server-side auth
      }
    })();
  }, []);

  const isSuperadmin = user?.access_level === 'superadmin';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // ✅ use path-only with centralized apiFetch
        const res = await apiFetch(`/api/groups/${id}`);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Failed to load group');

        // lightweight client-side check (server also enforces auth)
        const isOwner = user?.id === data?.owner_id;
        if (!isOwner && !isSuperadmin) {
          setAuthError('Not authorised to edit this group.');
        } else {
          setInitial(data || {});
        }
      } catch (e) {
        setAuthError(e.message || 'Failed to load group');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, isSuperadmin, user?.id]);

  const handleSubmit = async (formData) => {
    // Normalize (GroupForm may send strings)
    const bid_step = Number(formData.bid_step || initial?.bid_step || 100);
    const organiser_benefit_period = Number(
      formData.organiser_benefit_period != null
        ? formData.organiser_benefit_period
        : initial?.organiser_benefit_period || 0
    );
    const annual_interest_rate = formData.annual_interest_rate != null
      ? Number(formData.annual_interest_rate)
      : (initial?.annual_interest_rate != null ? Number(initial.annual_interest_rate) : 0.10);

    // Prefer the user-edited schedule from GroupForm; fallback to a generated one if missing.
    const scheduleOverrides = Array.isArray(formData.scheduleOverrides) && formData.scheduleOverrides.length
      ? formData.scheduleOverrides
      : buildStartingBidSchedule({
          duration: Number(formData.duration),
          contribution: Number(formData.contribution),
          frequency: String(formData.contribution_frequency || 'monthly').toLowerCase(),
          bidStep: bid_step,
          annualRate: annual_interest_rate,
          organiserPeriod: organiser_benefit_period,
          rounding: 'ceil',
        });

    const payload = {
      ...formData,
      annual_interest_rate,
      bid_step,
      organiser_benefit_period,
      scheduleOverrides,
      rounding: 'ceil',
    };

    // ✅ centralized apiFetch; no base URL
    const res = await apiFetch(`/api/groups/edit/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || 'Failed to update group');

    navigate('/groups');
  };

  if (loading) return <div className="main-content"><p>Loading…</p></div>;
  if (authError) return <div className="main-content"><p style={{ color: 'crimson' }}>{authError}</p></div>;

  return (
    <div className="main-content">
      <GroupForm
        initialData={initial || {}}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/groups')}
      />
    </div>
  );
}
